import type { UserCourse } from '@teachur/http-client';

interface ObjectiveLike {
  id?: number;
}

interface CourseWithLessons {
  lessons?: Array<{
    objectives?: ObjectiveLike[] | null;
  }> | null;
}

interface StudentWithCompletedObjectives {
  completedObjectives?: ObjectiveLike[] | null;
}

export interface StudentCourseProgressData {
  student?: unknown;
  course?: unknown;
}

export interface SelfAssessSummary {
  completedObjectivesCount: number;
  totalObjectivesCount: number;
  remainingObjectivesCount: number;
}

const getObjectiveIds = (objectives?: ObjectiveLike[] | null) => {
  return (objectives ?? [])
    .map((objective) => objective.id)
    .filter((id): id is number => typeof id === 'number');
};

const getCourseObjectiveIds = (course?: CourseWithLessons | null) => {
  return new Set((course?.lessons ?? []).flatMap((lesson) => getObjectiveIds(lesson.objectives)));
};

const getCompletedCourseObjectiveIds = (
  student: StudentWithCompletedObjectives | null | undefined,
  courseObjectiveIds: Set<number>
) => {
  return getObjectiveIds(student?.completedObjectives).filter((objectiveId) =>
    courseObjectiveIds.has(objectiveId)
  );
};

export const getSelfAssessSummary = (
  userCourse: UserCourse,
  progressData?: StudentCourseProgressData | null
): SelfAssessSummary => {
  const course = progressData?.course as CourseWithLessons | null | undefined;
  const student = progressData?.student as StudentWithCompletedObjectives | null | undefined;

  const courseObjectiveIds = getCourseObjectiveIds(course);
  const totalObjectivesCount = courseObjectiveIds.size;

  const completedObjectivesCount = userCourse.passed
    ? totalObjectivesCount
    : getCompletedCourseObjectiveIds(student, courseObjectiveIds).length;

  const remainingObjectivesCount = Math.max(totalObjectivesCount - completedObjectivesCount, 0);

  return {
    completedObjectivesCount,
    totalObjectivesCount,
    remainingObjectivesCount,
  };
};
